import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  MoreHorizontal,
  FileInput,
  Loader2,
  Eye,
  Pencil,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  DollarSign,
  CreditCard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CancellationReasonDialog } from '@/components/ui/cancellation-reason-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';

const statusConfig: Record<string, { color: string; icon: React.ElementType }> = {
  PENDING: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  APPROVED: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  PARTIALLY_PAID: { color: 'bg-yellow-100 text-yellow-700', icon: DollarSign },
  PAID: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  DISPUTED: { color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  CANCELLED: { color: 'bg-gray-100 text-gray-500', icon: XCircle },
};

export function SupplierInvoicesPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'trading:supplier-invoices:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [selectedStatus, setSelectedStatus] = useSessionState<string>(
    'trading:supplier-invoices:selectedStatus',
    'all'
  );
  const [page, setPage] = useState(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<string | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.tradingSupplierInvoice.list.useQuery({
    search: debouncedSearch || undefined,
    status: selectedStatus !== 'all' ? (selectedStatus as any) : undefined,
    page,
  });

  const utils = trpc.useUtils();

  const cancelMutation = trpc.tradingSupplierInvoice.cancel.useMutation({
    onSuccess: () => {
      utils.tradingSupplierInvoice.list.invalidate();
      showSuccessToast(t('finance.supplierInvoices.cancelled'));
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
    },
    onError: (error: { message: string }) => {
      showErrorToast(error.message);
    },
  });

  const approveMutation = trpc.tradingSupplierInvoice.approve.useMutation({
    onSuccess: () => {
      utils.tradingSupplierInvoice.list.invalidate();
      showSuccessToast(t('finance.supplierInvoices.approved'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const invoices = data?.invoices ?? [];
  const pagination = data?.pagination;

  const handleDeleteInvoice = (id: string) => {
    setInvoiceToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = (reason: string) => {
    if (invoiceToDelete) {
      cancelMutation.mutate({ id: invoiceToDelete, reason: reason || 'Cancelled by user' });
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return t('finance.supplierInvoices.statusPending');
      case 'APPROVED':
        return t('finance.supplierInvoices.statusApproved');
      case 'PARTIALLY_PAID':
        return t('finance.supplierInvoices.statusPartiallyPaid');
      case 'PAID':
        return t('finance.supplierInvoices.statusPaid');
      case 'DISPUTED':
        return t('finance.supplierInvoices.statusDisputed');
      case 'CANCELLED':
        return t('finance.supplierInvoices.statusCancelled');
      default:
        return status;
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const statuses = ['PENDING', 'APPROVED', 'PARTIALLY_PAID', 'PAID', 'DISPUTED', 'CANCELLED'];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('finance.supplierInvoices.title')}</h1>
          <p className="text-muted-foreground">{t('finance.supplierInvoices.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/trading/finance/supplier-invoices/new')}>
          <Plus className="mr-2 h-4 w-4" />
          {t('finance.supplierInvoices.addInvoice')}
        </Button>
      </div>

      {/* Cancel Confirmation Dialog */}
      <CancellationReasonDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('finance.supplierInvoices.cancelTitle')}
        description={t('finance.supplierInvoices.cancelDescription')}
        reasonLabel={t('finance.supplierInvoices.cancellationReason')}
        reasonPlaceholder={t('finance.supplierInvoices.cancellationReasonPlaceholder')}
        confirmLabel={t('finance.supplierInvoices.confirmCancel')}
        cancelLabel={tCommon('cancel')}
        onConfirm={confirmDelete}
        isLoading={cancelMutation.isPending}
      />

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('finance.supplierInvoices.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="border-border focus:border-foreground focus:ring-foreground/20 h-8 w-[180px] border focus:ring-1">
            <SelectValue placeholder={t('finance.supplierInvoices.selectStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('finance.supplierInvoices.allStatuses')}</SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {getStatusLabel(status)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && invoices.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileInput className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('finance.supplierInvoices.noInvoices')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('finance.supplierInvoices.noInvoicesDesc')}
          </p>
          <Button
            className="mt-4"
            onClick={() => navigate('/trading/finance/supplier-invoices/new')}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('finance.supplierInvoices.addInvoice')}
          </Button>
        </div>
      )}

      {/* Invoices Table */}
      {!isLoading && !error && invoices.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.supplierInvoices.invoiceNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.supplierInvoices.supplier')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {t('finance.supplierInvoices.status')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.supplierInvoices.invoiceDate')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.supplierInvoices.dueDate')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('finance.supplierInvoices.total')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('finance.supplierInvoices.balance')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {tCommon('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {invoices.map((invoice) => {
                  const config = statusConfig[invoice.status] || statusConfig.PENDING;
                  const StatusIcon = config?.icon ?? Clock;
                  const balance = Number(invoice.total) - Number(invoice.paidAmount);

                  return (
                    <tr key={invoice.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <Link
                          to={`/trading/finance/supplier-invoices/${invoice.id}`}
                          className="text-primary font-mono text-sm font-medium hover:underline"
                        >
                          {invoice.invoiceNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="text-sm font-medium">{invoice.supplierName}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
                        >
                          <StatusIcon className="h-3 w-3" />
                          {getStatusLabel(invoice.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{formatDate(invoice.invoiceDate)}</td>
                      <td className="px-4 py-3 text-sm">
                        {invoice.dueDate ? formatDate(invoice.dueDate) : '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        {formatCurrency(Number(invoice.total), invoice.currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        <span className={balance > 0 ? 'text-red-600' : 'text-green-600'}>
                          {formatCurrency(balance, invoice.currency)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                navigate(`/trading/finance/supplier-invoices/${invoice.id}`)
                              }
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              {tCommon('view')}
                            </DropdownMenuItem>
                            {invoice.status === 'PENDING' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate(
                                      `/trading/finance/supplier-invoices/${invoice.id}/edit`
                                    )
                                  }
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  {tCommon('edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => approveMutation.mutate(invoice.id)}
                                >
                                  <CheckCircle className="mr-2 h-4 w-4" />
                                  {t('finance.supplierInvoices.approve')}
                                </DropdownMenuItem>
                              </>
                            )}
                            {['APPROVED', 'PARTIALLY_PAID'].includes(invoice.status) && (
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate(
                                    `/trading/finance/payments/new?supplierInvoiceId=${invoice.id}`
                                  )
                                }
                              >
                                <CreditCard className="mr-2 h-4 w-4" />
                                {t('finance.supplierInvoices.recordPayment')}
                              </DropdownMenuItem>
                            )}
                            {invoice.status === 'PENDING' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => handleDeleteInvoice(invoice.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {tCommon('delete')}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {tCommon('showing')} {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} {tCommon('of')}{' '}
                {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="border-border hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('previous')}
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="border-border hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
